import { CoreEntityMap, CoreEntityType, NovelActTree, PromptMessage, PromptSchema, PromptType } from "#/index";
import { PromptInput } from "#/promptInput";
import { PromptParser } from "@/plugins/prompt-engine";
import { type JSONContent } from "novel";
import { LucideProps } from "lucide-react";
import { CodexEntryType } from "#/codexLibrary";
import React from "react";
import { codexBasicGroups } from "@/constants";




// tiptap json 转换为 text
// 递归遍历节点，将text类型的节点拼接起来,如果换行就在后面加一空格
export const tiptapJsonToText = (json: JSONContent): string => {
  let result = '';

  // 递归遍历节点
  const traverseNode = (node: JSONContent) => {
    // 处理文本内容
    if (node.type === 'text' && node.text) {
      result += node.text;
    }

    // 处理硬换行
    if (node.type === 'hardBreak') {
      result += ' ';
    }

    // 处理段落（在段落之间添加空格）
    if (node.type === 'paragraph' && node.content && node.content.length > 0) {
      // 如果段落有内容，递归处理子节点
      node.content.forEach(traverseNode);
      // 在段落结束后添加空格
      result += ' ';
    } else if (node.content) {
      // 其他有内容的节点，递归处理子节点
      node.content.forEach(traverseNode);
    }
  };

  // 开始遍历
  traverseNode(json);

  // 清理多余的空格
  return result.replace(/\s+/g, ' ').trim();
}

// 判断条目是否属于某个类别
export const getCodexEntryIsInCategory = (entry: CoreEntityMap<CoreEntityType.CodexEntries>, category: CoreEntityMap<CoreEntityType.CodexCategories>) => {
  if (!category.attributes.collects.includes(entry.attributes.type)) {
    return false
  }

  // 查看类别收集的Dropdown 是否包含该条目设置field的值
  let result = category.attributes.collectsFields.find(collectField => {
    return entry?.fields?.some(field => {
      return field.fieldId === collectField.id
        && field.content.select
        && field.content.select?.includes(collectField.value || '')
    })
  })
  if (result) return true;

  // 查看类别收集的Tags 是否包含该条目设置的Tags
  return entry.attributes.tags?.some(tag => category.attributes.tags.includes(tag)) || false
}


// 代码库条目分组类型
type CodexEntryGroup = {
  label: string;
  id?: string;
  type?: CodexEntryType;
  icon?: (props: Omit<LucideProps, 'ref'>) => React.ReactElement;
  category?: CoreEntityMap<CoreEntityType.CodexCategories>;
  children: CoreEntityMap<CoreEntityType.CodexEntries>[];
}

/**
 * 按类别分组代码库条目
 * 
 * 该方法将代码库条目按照两种方式进行分组：
 * 1. 自定义类别分组：基于用户创建的代码库类别，根据条目的类型、字段值和标签进行匹配
 * 2. 基础类型分组：按照条目的基础类型（角色、地点、物品等）进行分组
 * 
 * 分组逻辑：
 * - 自定义类别：使用 getCodexEntryIsInCategory 方法判断条目是否属于某个类别
 * - 基础类型：直接根据条目的 attributes.type 进行匹配
 * 
 * @param codexEntries 代码库条目数组
 * @param codexCategories 代码库类别数组
 * @returns 返回分组后的条目数组，每个分组包含：
 *   - label: 分组显示名称
 *   - type: 条目类型（仅基础分组有）
 *   - icon: 分组图标组件
 *   - category: 类别对象（仅自定义类别有）
 *   - children: 该分组下的条目数组
 */
export const getCodexEntryGroups = (
  codexEntries: CoreEntityMap<CoreEntityType.CodexEntries>[],
  codexCategories: CoreEntityMap<CoreEntityType.CodexCategories>[],
): CodexEntryGroup[] => {
  const groups = [...codexCategories.map(category => {
    return {
      label: category.attributes.name,
      id: category.id,
      category: category,
      children: [],
    }
  }), ...codexBasicGroups.map(group => {
    return {
      ...group,
      children: [],
    }
  })] as CodexEntryGroup[]

  codexEntries.forEach(entry => {
    groups.some(group => {
      if (group.category && getCodexEntryIsInCategory(entry, group.category!)) {
        group.children.push(entry)
        return true
      } else if (group.type && entry.attributes.type === group.type) {
        group.children.push(entry)
        return true
      }
      return false
    })
  })
  return groups.filter(group => group.children?.length)
}

// 递归 获取提示词中包含的组件
export const promptDeepIncludeComponent = (messages: PromptMessage[], prompts: PromptSchema[]) => {
  const result: PromptSchema[] = []
  const inputNames: string[] = []
  const comIds = result.map(component => component.id)
  messages.forEach(message => {
    const result = PromptParser.analyzeTemplate(message.text, 'include')
    result.forEach(item => {
      inputNames.push(item[0].replace(/["']/g, ''))
    })
  })

  prompts.forEach(prompt => {
    if (!comIds.includes(prompt.id)) {
      if (prompt.attributes.type === PromptType.Component && inputNames.includes(prompt.attributes.name)) {
        result.push(prompt)
        if (prompt.attributes.messages.length) {
          const childs = promptDeepIncludeComponent(prompt.attributes.messages, prompts)
          result.push(...childs)
        }
      }
    }
  })
  return result
}

// 获取提示词引入的输入组件和输入
export const promptIncludeComponentsAndInputs = (prompt: PromptSchema, prompts: PromptSchema[]) => {
  const result: {
    includeInputs: PromptInput[],
    referenceInputs: PromptInput[],
    includeInputNames: string[],
    includeComponents: PromptSchema[],
  } = {
    includeInputs: [],
    referenceInputs: [],
    includeInputNames: [],
    includeComponents: [],
  }

  const messages = prompt.attributes.messages
  let includeComponents: PromptSchema[] = promptDeepIncludeComponent(messages, prompts)
  // 系统内部排前面
  includeComponents = includeComponents.sort((a, b) => {
    if (a.relationships.owner.id === 'system') {
      return -1
    }
    if (b.relationships.owner.id === 'system') {
      return 1
    }
    return 0
  })
  result.referenceInputs = includeComponents.map(component => {
    const inputNames = component.attributes.messages.map((message) => {
      return PromptParser.analyzeTemplate(message.text, 'input').map(item => item[0]?.replace(/["']/g, ''))
    }).flat()

    return Object.values(component.inputs || {}).filter(input => inputNames.includes(input.name))
  }).flat()

  const includeInputNames: string[] = messages.flatMap(message => {
    const result = PromptParser.analyzeTemplate(message.text, 'input')
    return result.map(params => params[0]?.replace(/["']/g, ''))
  })
  result.includeInputs = Object.values(prompt.inputs || {}).filter(input => includeInputNames.includes(input.name))
  result.includeInputNames = includeInputNames
  result.includeComponents = includeComponents
  return result
}

export const getNovelActsTree = (novelId: string,
  novelActs: CoreEntityMap<CoreEntityType.NovelActs>[],
  novelChapters: CoreEntityMap<CoreEntityType.NovelChapters>[],
  novelScenes: CoreEntityMap<CoreEntityType.NovelScenes>[]): NovelActTree[] => {
  let actAutoNumber = 1;
  let chapterAutoNumber = 1;
  const chapterById = new Map(novelChapters.map((c) => [c.id, c]))
  const sceneById = new Map(novelScenes.map((s) => [s.id, s]))

  return novelActs.filter(act => act.relationships.project.id === novelId).map((act, actIndex) => {
    // 按 relationships 顺序取章/节，避免 filter 打乱目录
    const chapters = act.relationships.chapters
      .map((id) => chapterById.get(id))
      .filter(Boolean) as typeof novelChapters
    return {
      id: act.id,
      title: act.attributes.title,
      realIndex: actIndex,
      autoNumber: act.attributes.numerate ? actAutoNumber++ : undefined,
      novelId: novelId,
      parentId: novelId,
      shortcutName: act.attributes.numerate ? `第${actAutoNumber}篇` : act.attributes.title || '未命名篇',
      fullName: `${act.attributes.numerate
        ? `第${actAutoNumber}篇${act.attributes.title ? `:${act.attributes.title}` : ''}`
        : act.attributes.title || '未命名篇'
        }`,
      wordCount: 0,
      data: act,
      children: chapters.map((chapter, chapterIndex) => {
        const scenes = chapter.relationships.scenes
          .map((id) => sceneById.get(id))
          .filter(Boolean) as typeof novelScenes
        const chapterFullName = `${chapter.attributes.numerate
          ? `第${chapterAutoNumber}章${chapter.attributes.title ? `:${chapter.attributes.title}` : ''}`
          : chapter.attributes.title || '未命名章节'
          }`
        return {
          id: chapter.id,
          title: chapter.attributes.title,
          realIndex: chapterIndex,
          autoNumber: chapter.attributes.numerate ? chapterAutoNumber++ : undefined,
          novelId: novelId,
          shortcutName: chapter.attributes.numerate ? `第${chapterAutoNumber}章` : chapter.attributes.title || '未命名章节',
          fullName: chapterFullName,
          parentId: act.id,
          wordCount: 0,
          data: chapter,
          children: scenes.map((scene, sceneIndex) => {
            return {
              id: scene.id,
              title: scene.attributes.title,
              realIndex: sceneIndex,
              novelId: novelId,
              shortcutName: scene.attributes.title || `节 ${sceneIndex + 1}`,
              fullName: `${chapterFullName} - 节${sceneIndex + 1}${scene.attributes.title ? `:${scene.attributes.title}` : ''}`,
              parentId: chapter.id,
              wordCount: 0,
              data: scene,
            }
          }),
          childIds: scenes.map(scene => scene.id),
        }
      }),
      childIds: chapters.map(chapter => chapter.id),
    }
  })
}

// 展开小说树形结构 映射为 key-value 结构
export const getNovelActTreeMap = (novelActTree: NovelActTree[]): Map<string, NovelActTree> => {
  const result: Map<string, NovelActTree> = new Map()

  const traverse = (act: NovelActTree) => {
    result.set(act.id, act)
    if (act.children) {
      act.children.forEach(child => {
        traverse(child)
      })
    }
  }

  novelActTree.forEach(act => {
    traverse(act)
  })

  return result
}

// 在此增加一个按所给位置ID获取位置前的NovelActTree的函数，以及位置后的NovelActTree的函数
const cloneNodeWithChildren = (node: NovelActTree, children: NovelActTree[] = []): NovelActTree => {
  const cloned: NovelActTree = {
    ...node,
    children: children.length ? children : undefined,
    childIds: children.length ? children.map(child => child.id) : undefined
  };
  return cloned;
};

const collectTreesBefore = (trees: NovelActTree[], id: string): { nodes: NovelActTree[]; found: boolean } => {
  const result: NovelActTree[] = [];

  for (const tree of trees) {
    if (tree.id === id) {
      return { nodes: result, found: true };
    }

    if (tree.children?.length) {
      const { nodes: childNodes, found } = collectTreesBefore(tree.children, id);
      if (found) {
        const cloned = cloneNodeWithChildren(tree, childNodes);
        result.push(cloned);
        return { nodes: result, found: true };
      }
    }

    result.push(tree);
  }

  return { nodes: result, found: false };
};

const collectTreesAfter = (trees: NovelActTree[], id: string): { nodes: NovelActTree[]; found: boolean } => {
  const result: NovelActTree[] = [];

  for (let index = 0; index < trees.length; index++) {
    const tree = trees[index];

    if (tree.id === id) {
      result.push(tree);
      for (let i = index + 1; i < trees.length; i++) {
        result.push(trees[i]);
      }
      return { nodes: result, found: true };
    }

    if (tree.children?.length) {
      const { nodes: childNodes, found } = collectTreesAfter(tree.children, id);
      if (found) {
        const cloned = cloneNodeWithChildren(tree, childNodes);
        result.push(cloned);
        for (let i = index + 1; i < trees.length; i++) {
          result.push(trees[i]);
        }
        return { nodes: result, found: true };
      }
    }
  }

  return { nodes: result, found: false };
};

export const getPreviousTreeDataById = (trees: NovelActTree[], id: string): NovelActTree[] => {
  const { nodes } = collectTreesBefore(trees, id);
  return nodes;
};

export const getNextTreeDataById = (trees: NovelActTree[], id: string): NovelActTree[] => {
  const { nodes } = collectTreesAfter(trees, id);
  return nodes;
};